import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useMatches } from 'react-router-dom';

import { useRouteEntityName } from '@hooks/useDocumentTitle/entityName';

import {
  composePageContext,
  type PageContextContributorId,
  type RegisteredPageContextContribution,
} from './composePageContext';
import { resolvePageContextRoute } from './routeMetadata';
import type { PageContextContribution, PageContextSnapshot } from './types';

interface PageContextReadContextValue {
  pageContext?: PageContextSnapshot;
  capturePageContext: () => PageContextSnapshot | undefined;
}

interface PageContextContributionContextValue {
  pageScope?: string;
  registerContribution: (
    contributorId: PageContextContributorId,
    owner: string,
    pageScope: string | undefined,
    contribution: PageContextContribution,
  ) => void;
  removeContribution: (contributorId: PageContextContributorId) => void;
}

interface PageContextContributionOptions extends PageContextContribution {
  owner: string;
}

const noop = (): void => undefined;
const EMPTY_READ_CONTEXT: PageContextReadContextValue = {
  capturePageContext: () => undefined,
};
const EMPTY_CONTRIBUTION_CONTEXT: PageContextContributionContextValue = {
  registerContribution: noop,
  removeContribution: noop,
};

// Keep readers and contributors separate. Publishing a contribution updates
// Rudder AI without forcing every page contributor to render again.
const PageContextReadContext = createContext<PageContextReadContextValue>(EMPTY_READ_CONTEXT);
const PageContextContributionContext = createContext<PageContextContributionContextValue>(
  EMPTY_CONTRIBUTION_CONTEXT,
);

/** Owns the live Page Context assembled from the matched route and rendered page. */
export const PageContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const matches = useMatches();
  const { pathname } = useLocation();
  const routeEntityName = useRouteEntityName();
  const route = useMemo(() => resolvePageContextRoute(matches), [matches]);
  // The query string is excluded because tab changes update the contribution
  // within the same page scope instead of creating a second page instance.
  const pageScope = route ? `${route.type}:${pathname}` : undefined;
  const registeredContributions = useRef(
    new Map<PageContextContributorId, RegisteredPageContextContribution>(),
  );
  const [compositionVersion, setCompositionVersion] = useState(0);
  const routeRef = useRef(route);
  const pageScopeRef = useRef(pageScope);
  const routeEntityNameRef = useRef(routeEntityName);
  routeRef.current = route;
  pageScopeRef.current = pageScope;
  routeEntityNameRef.current = routeEntityName;

  const registerContribution = useCallback(
    (
      contributorId: PageContextContributorId,
      owner: string,
      contributionScope: string | undefined,
      contribution: PageContextContribution,
    ) => {
      if (!contributionScope) {
        if (registeredContributions.current.delete(contributorId)) {
          setCompositionVersion((version) => version + 1);
        }
        return;
      }
      const signature = JSON.stringify({ owner, pageScope: contributionScope, contribution });
      if (registeredContributions.current.get(contributorId)?.signature === signature) {
        return;
      }
      registeredContributions.current.set(contributorId, {
        owner,
        pageScope: contributionScope,
        contribution,
        signature,
      });
      setCompositionVersion((version) => version + 1);
    },
    [],
  );

  const removeContribution = useCallback((contributorId: PageContextContributorId) => {
    if (registeredContributions.current.delete(contributorId)) {
      setCompositionVersion((version) => version + 1);
    }
  }, []);

  const pageContext = useMemo(
    () => composePageContext(route, pageScope, registeredContributions.current, routeEntityName),
    [compositionVersion, route, routeEntityName, pageScope],
  );
  const capturePageContext = useCallback(
    () =>
      composePageContext(
        routeRef.current,
        pageScopeRef.current,
        registeredContributions.current,
        routeEntityNameRef.current,
      ),
    [],
  );

  const readValue = useMemo(
    () => ({ pageContext, capturePageContext }),
    [pageContext, capturePageContext],
  );
  const contributionValue = useMemo(
    () => ({ pageScope, registerContribution, removeContribution }),
    [pageScope, registerContribution, removeContribution],
  );

  return (
    <PageContextContributionContext.Provider value={contributionValue}>
      <PageContextReadContext.Provider value={readValue}>
        {children}
      </PageContextReadContext.Provider>
    </PageContextContributionContext.Provider>
  );
};

/** Read the displayed Page Context or capture its latest send-time snapshot. */
export const useResolvedPageContext = (): Pick<
  PageContextReadContextValue,
  'pageContext' | 'capturePageContext'
> => {
  const { pageContext, capturePageContext } = useContext(PageContextReadContext);
  return { pageContext, capturePageContext };
};

/** Register the page facts owned by the calling rendered component. */
export const usePageContextContribution = ({
  owner,
  ...contribution
}: PageContextContributionOptions): void => {
  const { pageScope, registerContribution, removeContribution } = useContext(
    PageContextContributionContext,
  );
  const contributorId = useRef<PageContextContributorId>();
  if (!contributorId.current) {
    contributorId.current = Symbol(owner);
  }
  const id = contributorId.current;

  useEffect(() => {
    registerContribution(id, owner, pageScope, contribution);
  }, [id, owner, pageScope, contribution, registerContribution]);

  useEffect(
    () => () => {
      removeContribution(id);
    },
    [id, removeContribution],
  );
};
